import { useContext, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { alpha } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import axiosInstance from '../../lib/axios';
import { getUser } from '../../utils/auth';
import { palette, typography, theme } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { AnalyticsDashboard } from './analytics';
import { ProfitAnalyticsDashboard } from './profit-analytics';
import { B2BDashboard } from './b2b';
import { SalesExpensesDashboard } from './sales-expenses';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useB2BAccess, useModuleAccess } from '../../hooks/useModuleAccess';
import { BlurredFeatureOverlay } from '../../components/BlurredFeatureOverlay';
import { SwipeableStatCard } from '../../components/mobile/SwipeableStatCard';

interface DashboardStats {
  todaySales: { count: number; total: number };
  weekSales: { count: number; total: number };
  monthSales: { count: number; total: number };
  pendingReservations: number;
  lowStockItems: number;
  recentSales: Array<{
    id: string;
    receiptNumber: string;
    total: number;
    createdAt: string;
    items: number;
  }>;
  lowStockProducts: Array<{
    id: string;
    variantName: string;
    productName: string;
    quantity: number;
  }>;
}

const fetchDashboardData = async (): Promise<DashboardStats> => {
  const user = getUser();
  const locationId = user?.locationId;
  const isAdmin = user?.isAdmin || false;

  // Non-admin users without a location cannot view dashboard
  if (!locationId && !isAdmin) {
    throw new Error('No store associated with user');
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(today);
  monthAgo.setMonth(monthAgo.getMonth() - 1);

  // Build query params - omit locationId for admins to get org-wide data
  // Use /orders endpoint for unified order system
  const params = new URLSearchParams();
  if (locationId) {
    params.append('locationId', locationId);
  }
  // Include completed/paid orders for sales statistics
  params.append('paymentStatuses', 'paid');
  params.append('paymentStatuses', 'partially_paid');

  const ordersResponse = await axiosInstance.get(`/orders${params.toString() ? `?${params.toString()}` : ''}`);
  const allOrders = Array.isArray(ordersResponse.data)
    ? ordersResponse.data
    : ordersResponse.data.data || [];

  const todaySales = allOrders.filter((order: any) => new Date(order.createdAt) >= today);
  const weekSales = allOrders.filter((order: any) => new Date(order.createdAt) >= weekAgo);
  const monthSales = allOrders.filter((order: any) => new Date(order.createdAt) >= monthAgo);

  const todayTotal = todaySales.reduce((sum: number, order: any) => sum + Number(order.totalAmount), 0);
  const weekTotal = weekSales.reduce((sum: number, order: any) => sum + Number(order.totalAmount), 0);
  const monthTotal = monthSales.reduce((sum: number, order: any) => sum + Number(order.totalAmount), 0);

  const recentSales = allOrders
    .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5)
    .map((order: any) => ({
      id: order.id,
      receiptNumber: order.receiptNumber || order.trackingNumber,
      total: Number(order.totalAmount),
      createdAt: order.createdAt,
      items: order.items?.length || order.totalItems || 0,
    }));

  // Fetch pending orders (reservations) - orders with pending fulfillment status
  const pendingParams = new URLSearchParams();
  if (locationId) {
    pendingParams.append('locationId', locationId);
  }
  pendingParams.append('fulfillmentStatuses', 'pending');
  pendingParams.append('fulfillmentStatuses', 'waiting_transfer');
  pendingParams.append('fulfillmentStatuses', 'waiting_admin_action');
  pendingParams.append('fulfillmentStatuses', 'confirmed');

  const pendingOrdersResponse = await axiosInstance.get(`/orders${pendingParams.toString() ? `?${pendingParams.toString()}` : ''}`);
  const allPendingOrders = Array.isArray(pendingOrdersResponse.data)
    ? pendingOrdersResponse.data
    : pendingOrdersResponse.data.data || [];
  const pendingReservations = allPendingOrders.length;

  const locationFilter = locationId ? `locationId=${locationId}` : '';
  const inventoryResponse = await axiosInstance.get(`/inventory${locationFilter ? `?${locationFilter}` : ''}`);
  const inventory = Array.isArray(inventoryResponse.data)
    ? inventoryResponse.data
    : inventoryResponse.data.data || [];

  const lowStockProducts = inventory
    .filter((item: any) => item.quantity < 10)
    .map((item: any) => ({
      id: item.id,
      variantName: item.variant?.name || 'Unknown',
      productName: item.variant?.product?.name || 'Unknown',
      quantity: item.quantity,
    }))
    .slice(0, 5);

  return {
    todaySales: { count: todaySales.length, total: todayTotal },
    weekSales: { count: weekSales.length, total: weekTotal },
    monthSales: { count: monthSales.length, total: monthTotal },
    pendingReservations,
    lowStockItems: lowStockProducts.length,
    recentSales,
    lowStockProducts,
  };
};

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const [activeTab, setActiveTab] = useState(0);
  const { notifications } = useNotifications();
  const animations = useAnimationStyles();
  const { hasAccess: hasB2BAccess } = useB2BAccess();
  const { hasAccess: hasSalesModule } = useModuleAccess('sales');

  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboardData,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  // Refetch dashboard when new reservation notification arrives
  useEffect(() => {
    const latestNotification = notifications[0];
    if (latestNotification?.type === 'NEW_RESERVATION') {
      refetch();
    }
  }, [notifications, refetch]);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    surfaceHover: palette.surfaceHover,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    surfaceHover: palette.light.surfaceHover,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
    }).format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('fr-DZ', { day: '2-digit', month: 'short' }),
      time: date.toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const cardStyles = {
    backgroundColor: colors.surface,
    border: `2px solid ${colors.border}`,
    borderRadius: theme.radius.sm,
    p: 2.5,
    position: 'relative',
    overflow: 'hidden',
  };

  // Prepare stat cards data for SwipeableStatCard component
  const statCardsData = stats ? [
    {
      id: 'today-sales',
      title: "TODAY'S SALES",
      value: stats.todaySales.count.toString(),
      subValue: formatCurrency(stats.todaySales.total),
      color: palette.primary,
      trend: {
        direction: 'up' as const,
        value: '+12%',
      },
    },
    {
      id: 'week-sales',
      title: 'THIS WEEK',
      value: stats.weekSales.count.toString(),
      subValue: formatCurrency(stats.weekSales.total),
      color: palette.success,
    },
    {
      id: 'month-sales',
      title: 'THIS MONTH',
      value: stats.monthSales.count.toString(),
      subValue: formatCurrency(stats.monthSales.total),
      color: palette.info,
    },
    {
      id: 'pending-reservations',
      title: 'PENDING RESERVATIONS',
      value: stats.pendingReservations.toString(),
      color: palette.warning,
    },
    {
      id: 'low-stock',
      title: 'LOW STOCK ALERT',
      value: stats.lowStockItems.toString(),
      color: palette.error,
    },
  ] : [];

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress sx={{ color: palette.primary }} />
        <Typography
          variant="body2"
          sx={{
            color: colors.textMuted,
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.sm,
            textTransform: 'uppercase',
            letterSpacing: typography.letterSpacing.widest,
          }}
        >
          LOADING DASHBOARD...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert
          severity="error"
          sx={{
            borderRadius: theme.radius.sm,
            backgroundColor: alpha(palette.error, 0.1),
            color: palette.error,
            border: `2px solid ${palette.error}`,
            '& .MuiAlert-icon': { color: palette.error },
            fontFamily: typography.fontFamily.mono,
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => refetch()}
              sx={{
                fontWeight: typography.fontWeight.bold,
                textTransform: 'uppercase',
                fontFamily: typography.fontFamily.mono,
              }}
            >
              RETRY
            </Button>
          }
        >
          {error instanceof Error ? error.message : 'SYSTEM ERROR: Failed to load dashboard data'}
        </Alert>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box p={3}>
        <Alert
          severity="info"
          sx={{
            borderRadius: theme.radius.sm,
            backgroundColor: alpha(palette.info, 0.1),
            color: palette.info,
            border: `2px solid ${palette.info}`,
            fontFamily: typography.fontFamily.mono,
          }}
        >
          NO DATA AVAILABLE
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...animations.fadeIn,
      }}
    >
      {/* Dashboard Header */}
      <Box
        sx={{
          mb: 4,
          ...animations.slideDown,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DashboardOutlinedIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                DASHBOARD
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              DASHBOARD
            </Typography>
          </Box>
          <Tooltip title="REFRESH DATA">
            <IconButton
              onClick={() => refetch()}
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.xs,
                color: colors.textPrimary,
                '&:hover': {
                  borderColor: palette.primary,
                  backgroundColor: alpha(palette.primary, 0.1),
                  color: palette.primary,
                },
              }}
            >
              <RefreshOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Industrial Tabs */}
      <Box
        sx={{
          borderBottom: `2px solid ${colors.border}`,
          mb: 3,
          backgroundColor: colors.surfaceElevated,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          ...animations.slideUp(0.1),
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_e, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: colors.textMuted,
              fontWeight: typography.fontWeight.bold,
              textTransform: 'uppercase',
              fontFamily: typography.fontFamily.mono,
              fontSize: typography.fontSize.xs,
              letterSpacing: typography.letterSpacing.widest,
              minHeight: 56,
              '&.Mui-selected': {
                color: palette.primary,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: palette.primary,
              height: 4,
            },
          }}
        >
          <Tab icon={<DashboardOutlinedIcon />} iconPosition="start" label="OVERVIEW" />
          <Tab icon={<ShowChartOutlinedIcon />} iconPosition="start" label="ANALYTICS" />
          <Tab icon={<TrendingUpOutlinedIcon />} iconPosition="start" label="PROFITABILITY" />
          {hasSalesModule && (
            <Tab icon={<AccountBalanceWalletOutlinedIcon />} iconPosition="start" label="SALES & EXPENSES" />
          )}
          {hasB2BAccess && (
            <Tab icon={<BusinessIcon />} iconPosition="start" label="B2B PARTNERS" />
          )}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 ? (
        <Box>
          {/* Sales Metrics - SwipeableStatCard (Mobile-optimized) */}
          <SwipeableStatCard cards={statCardsData.slice(0, 3)} />

          {/* Alert Panels */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Pending Reservations */}
            <Box
              sx={{
                ...cardStyles,
                ...animations.scaleIn(0.44),
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                cursor: 'pointer',
                borderColor: stats.pendingReservations > 0 ? palette.warning : colors.border,
                borderLeftWidth: stats.pendingReservations > 0 ? 4 : 2,
                borderLeftColor: stats.pendingReservations > 0 ? palette.warning : colors.border,
                backgroundColor: stats.pendingReservations > 0 ? alpha(palette.warning, 0.05) : colors.surface,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(palette.warning, 0.2)}`,
                  borderColor: palette.warning,
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ShoppingCartOutlinedIcon sx={{ color: palette.warning, fontSize: 32 }} />
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                        fontWeight: typography.fontWeight.bold,
                      }}
                    >
                      PENDING RESERVATIONS
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: stats.pendingReservations > 0 ? palette.warning : colors.textPrimary,
                      }}
                    >
                      {stats.pendingReservations}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate('/app/reservations?status=pending')}
                  sx={{
                    color: palette.primary,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    fontSize: typography.fontSize.xs,
                    fontFamily: typography.fontFamily.mono,
                    border: `1px solid ${colors.border}`,
                    '&:hover': {
                      backgroundColor: alpha(palette.primary, 0.1),
                      borderColor: palette.primary,
                    },
                  }}
                >
                  VIEW
                </Button>
              </Box>
            </Box>

            {/* Low Stock Items */}
            <Box
              sx={{
                ...cardStyles,
                ...animations.scaleIn(0.52),
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                cursor: 'pointer',
                borderColor: stats.lowStockItems > 0 ? palette.error : colors.border,
                borderLeftWidth: stats.lowStockItems > 0 ? 4 : 2,
                borderLeftColor: stats.lowStockItems > 0 ? palette.error : colors.border,
                backgroundColor: stats.lowStockItems > 0 ? alpha(palette.error, 0.05) : colors.surface,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${alpha(palette.error, 0.2)}`,
                  borderColor: palette.error,
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <WarningAmberOutlinedIcon sx={{ color: palette.error, fontSize: 32 }} />
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: colors.textMuted,
                        fontFamily: typography.fontFamily.mono,
                        fontSize: typography.fontSize.xs,
                        letterSpacing: typography.letterSpacing.widest,
                        fontWeight: typography.fontWeight.bold,
                      }}
                    >
                      LOW STOCK ALERT
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.extrabold,
                        color: stats.lowStockItems > 0 ? palette.error : colors.textPrimary,
                      }}
                    >
                      {stats.lowStockItems}
                    </Typography>
                  </Box>
                </Box>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate('/app/inventory')}
                  sx={{
                    color: palette.primary,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    fontSize: typography.fontSize.xs,
                    fontFamily: typography.fontFamily.mono,
                    border: `1px solid ${colors.border}`,
                    '&:hover': {
                      backgroundColor: alpha(palette.primary, 0.1),
                      borderColor: palette.primary,
                    },
                  }}
                >
                  VIEW
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Data Streams - Recent Sales & Low Stock */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Recent Sales Log */}
            <Box sx={{
              ...cardStyles,
              ...animations.scaleIn(0.6),
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 16px ${alpha(palette.primary, 0.15)}`,
              },
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.textPrimary,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wide,
                    }}
                  >
                    TRANSACTION LOG
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      textTransform: 'uppercase',
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    LATEST ENTRIES
                  </Typography>
                </Box>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                  onClick={() => navigate('/app/sales')}
                  sx={{
                    color: palette.primary,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    fontSize: typography.fontSize.xs,
                    fontFamily: typography.fontFamily.mono,
                    border: `1px solid ${colors.border}`,
                    '&:hover': {
                      backgroundColor: alpha(palette.primary, 0.1),
                      borderColor: palette.primary,
                    },
                  }}
                >
                  VIEW ALL
                </Button>
              </Box>

              {stats.recentSales.length === 0 ? (
                <Box
                  sx={{
                    py: 6,
                    textAlign: 'center',
                    backgroundColor: colors.surfaceElevated,
                    border: `1px solid ${colors.border}`,
                    borderRadius: theme.radius.xs,
                  }}
                >
                  <ReceiptLongOutlinedIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      textTransform: 'uppercase',
                    }}
                  >
                    NO TRANSACTIONS RECORDED
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {stats.recentSales.map((sale) => {
                    const saleDate = formatDateTime(sale.createdAt);
                    return (
                      <Box
                        key={sale.id}
                        onClick={() => navigate(`/app/sales/show/${sale.id}`)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          backgroundColor: colors.surfaceElevated,
                          border: `1px solid ${colors.border}`,
                          borderLeft: `4px solid ${palette.primary}`,
                          cursor: 'pointer',
                          transition: theme.transitions.fast,
                          '&:hover': {
                            backgroundColor: alpha(palette.primary, 0.05),
                            borderLeftColor: palette.primary,
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                          <ReceiptLongOutlinedIcon sx={{ color: palette.primary, fontSize: 20 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                fontWeight: typography.fontWeight.bold,
                                color: colors.textPrimary,
                                fontSize: typography.fontSize.sm,
                                letterSpacing: typography.letterSpacing.wide,
                              }}
                            >
                              {sale.receiptNumber}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.textMuted,
                                fontFamily: typography.fontFamily.mono,
                                fontSize: typography.fontSize.xs,
                              }}
                            >
                              {saleDate.date} • {saleDate.time} • {sale.items} ITEMS
                            </Typography>
                          </Box>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontWeight: typography.fontWeight.extrabold,
                            color: palette.success,
                            fontSize: typography.fontSize.sm,
                          }}
                        >
                          {formatCurrency(sale.total)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Low Stock Monitor */}
            <Box sx={{
              ...cardStyles,
              ...animations.scaleIn(0.68),
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 16px ${alpha(palette.error, 0.15)}`,
              },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <ErrorOutlineIcon sx={{ color: palette.error, fontSize: 28 }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: typography.fontFamily.display,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.textPrimary,
                      textTransform: 'uppercase',
                      letterSpacing: typography.letterSpacing.wide,
                      lineHeight: 1,
                    }}
                  >
                    STOCK ALERT
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.textMuted,
                      fontFamily: typography.fontFamily.mono,
                      textTransform: 'uppercase',
                      fontSize: typography.fontSize.xs,
                    }}
                  >
                    CRITICAL LEVELS
                  </Typography>
                </Box>
              </Box>

              {stats.lowStockProducts.length === 0 ? (
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    backgroundColor: colors.surfaceElevated,
                    border: `1px solid ${colors.border}`,
                    borderRadius: theme.radius.xs,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: palette.success,
                      fontFamily: typography.fontFamily.mono,
                      textTransform: 'uppercase',
                      fontWeight: typography.fontWeight.bold,
                    }}
                  >
                    ALL SYSTEMS NORMAL
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {stats.lowStockProducts.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 1.5,
                        backgroundColor: colors.surfaceElevated,
                        border: `1px solid ${colors.border}`,
                        borderLeft: `4px solid ${item.quantity === 0 ? palette.error : palette.warning}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: typography.fontWeight.bold,
                            color: colors.textPrimary,
                            fontSize: typography.fontSize.sm,
                            fontFamily: typography.fontFamily.display,
                          }}
                        >
                          {item.productName}
                        </Typography>
                        <Chip
                          label={item.quantity === 0 ? 'EMPTY' : item.quantity}
                          size="small"
                          sx={{
                            backgroundColor: alpha(item.quantity === 0 ? palette.error : palette.warning, 0.1),
                            color: item.quantity === 0 ? palette.error : palette.warning,
                            fontWeight: typography.fontWeight.bold,
                            fontSize: typography.fontSize.xs,
                            fontFamily: typography.fontFamily.mono,
                            border: `1px solid ${item.quantity === 0 ? palette.error : palette.warning}`,
                            height: 22,
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                        }}
                      >
                        {item.variantName}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Quick Access Panel */}
          <Box sx={{
            ...cardStyles,
            ...animations.scaleIn(0.76),
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 4px 16px ${alpha(palette.primary, 0.15)}`,
            },
          }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                textTransform: 'uppercase',
                letterSpacing: typography.letterSpacing.wide,
                mb: 0.5,
              }}
            >
              QUICK ACCESS
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                textTransform: 'uppercase',
                fontSize: typography.fontSize.xs,
                display: 'block',
                mb: 3,
              }}
            >
              COMMON OPERATIONS
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<PointOfSaleOutlinedIcon />}
                onClick={() => navigate('/app/pos')}
                sx={{
                  backgroundColor: palette.primary,
                  color: '#FFFFFF',
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  fontSize: typography.fontSize.sm,
                  fontFamily: typography.fontFamily.mono,
                  px: 3,
                  py: 1.5,
                  borderRadius: theme.radius.sm,
                  '&:hover': {
                    backgroundColor: alpha(palette.primary, 0.8),
                  },
                }}
              >
                NEW SALE (POS)
              </Button>
              <Button
                variant="outlined"
                startIcon={<Inventory2OutlinedIcon />}
                onClick={() => navigate('/app/inventory')}
                sx={{
                  borderColor: colors.border,
                  color: colors.textPrimary,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  fontSize: typography.fontSize.sm,
                  fontFamily: typography.fontFamily.mono,
                  px: 3,
                  py: 1.5,
                  borderRadius: theme.radius.sm,
                  '&:hover': {
                    borderColor: palette.primary,
                    backgroundColor: alpha(palette.primary, 0.1),
                    color: palette.primary,
                  },
                }}
              >
                CHECK INVENTORY
              </Button>
              <Button
                variant="outlined"
                startIcon={<ShoppingCartOutlinedIcon />}
                onClick={() => navigate('/app/reservations')}
                sx={{
                  borderColor: colors.border,
                  color: colors.textPrimary,
                  fontWeight: typography.fontWeight.bold,
                  textTransform: 'uppercase',
                  fontSize: typography.fontSize.sm,
                  fontFamily: typography.fontFamily.mono,
                  px: 3,
                  py: 1.5,
                  borderRadius: theme.radius.sm,
                  '&:hover': {
                    borderColor: palette.primary,
                    backgroundColor: alpha(palette.primary, 0.1),
                    color: palette.primary,
                  },
                }}
              >
                VIEW RESERVATIONS
              </Button>
            </Box>
          </Box>
        </Box>
      ) : activeTab === 1 ? (
        <BlurredFeatureOverlay
          feature="Analytics Dashboard"
          requiredTier="professional"
          minHeight={400}
        >
          <AnalyticsDashboard />
        </BlurredFeatureOverlay>
      ) : activeTab === 2 ? (
        <BlurredFeatureOverlay
          feature="Profitability Analytics"
          requiredTier="professional"
          minHeight={400}
        >
          <ProfitAnalyticsDashboard />
        </BlurredFeatureOverlay>
      ) : activeTab === 3 && hasSalesModule ? (
        <BlurredFeatureOverlay
          feature="Sales & Expenses Analytics"
          requiredTier="professional"
          minHeight={400}
        >
          <SalesExpensesDashboard />
        </BlurredFeatureOverlay>
      ) : activeTab === (hasSalesModule ? 4 : 3) && hasB2BAccess ? (
        <BlurredFeatureOverlay
          feature="B2B Partners Dashboard"
          requiredTier="professional"
          minHeight={400}
        >
          <B2BDashboard />
        </BlurredFeatureOverlay>
      ) : null}
    </Box>
  );
};
